import { useState, useEffect, useCallback } from 'react';
import versionService from '../services/versionService';

/**
 * Hook for managing app updates and refresh notifications
 */
const useAppUpdates = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [updateData, setUpdateData] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Handle version service events
  const handleVersionEvent = useCallback((event, data) => {
    // Version event received
    
    switch (event) {
      case 'update-available':
        setUpdateData(data);
        setUpdateAvailable(true);
        setIsVisible(true);
        
        // Auto-refresh for all updates (immediate notification)
        // User can dismiss if they want to continue working
        setAutoRefresh(false); // Don't auto-refresh, but show notification immediately
        break;
        
      default:
        break;
    }
  }, []);

  // Initialize version service
  useEffect(() => {
    versionService.addListener(handleVersionEvent);
    versionService.init();

    // Check immediately on mount
    setTimeout(() => {
      versionService.forceCheck();
    }, 2000); // Wait 2 seconds after mount to avoid blocking initial load

    // Listen for route changes to check for updates
    const handleRouteChange = () => {
      setTimeout(() => {
        versionService.forceCheck();
      }, 1000);
    };

    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', handleRouteChange);
    
    // Listen for pushstate/replacestate (React Router navigation)
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(...args) {
      originalPushState.apply(window.history, args);
      handleRouteChange();
    };
    
    window.history.replaceState = function(...args) {
      originalReplaceState.apply(window.history, args);
      handleRouteChange();
    };

    return () => {
      versionService.removeListener(handleVersionEvent);
      window.removeEventListener('popstate', handleRouteChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [handleVersionEvent]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    versionService.refresh();
  }, []);

  // Handle force refresh (clears cache)
  const handleForceRefresh = useCallback(() => {
    versionService.forceRefresh();
  }, []);

  // Handle dismiss
  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    setUpdateAvailable(false);
    setAutoRefresh(false);
    setCountdown(0);
  }, []);

  // Handle later - hides banner for the session (sessionStorage is handled in UpdateNotification)
  const handleLater = useCallback(() => {
    setIsVisible(false);
    // Keep updateAvailable true but don't re-show automatically
    // The banner component handles sessionStorage to persist the "later" decision
    setAutoRefresh(false);
    setCountdown(0);
  }, []);

  // Force check for updates
  const checkForUpdates = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      versionService.manualCheck();
    } else {
      versionService.forceCheck();
    }
  }, []);

  return {
    updateAvailable,
    isVisible,
    updateData,
    autoRefresh,
    countdown,
    handleRefresh,
    handleForceRefresh,
    handleDismiss,
    handleLater,
    checkForUpdates
  };
};

export default useAppUpdates;
